import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft } from 'react-icons/fa';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 120px var(--space-md) 80px;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: var(--space-sm);
  color: var(--primary-600);
  text-decoration: none;
  margin-bottom: var(--space-xl);
  
  &:hover {
    color: var(--primary-700);
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: var(--space-lg);
  color: var(--secondary-900);
`;

const Content = styled.div`
  line-height: 1.8;
  font-size: 1.125rem;
  color: var(--secondary-700);

  h2 {
    margin: var(--space-2xl) 0 var(--space-lg);
    color: var(--secondary-900);
    font-size: 1.75rem;
  }

  h3 {
    margin: var(--space-xl) 0 var(--space-md);
    color: var(--secondary-900);
    font-size: 1.25rem;
  }

  p {
    margin-bottom: var(--space-lg);
  }

  ul, ol {
    margin-bottom: var(--space-lg);
    padding-left: var(--space-xl);
  }

  li {
    margin-bottom: var(--space-sm);
  }

  strong {
    color: var(--secondary-900);
    font-weight: 600;
  }

  .contact-info {
    background: var(--secondary-50);
    padding: var(--space-lg);
    border-radius: var(--radius-md);
    margin: var(--space-lg) 0;
    border-left: 4px solid var(--primary-600);
  }

  .legal-section {
    margin: var(--space-xl) 0;
    padding: var(--space-lg);
    background: var(--primary-50);
    border-radius: var(--radius-md);
    border-left: 4px solid var(--primary-600);
  }
`;

const Imprint = () => {
  const { t } = useTranslation();
  
  return (
    <>
      <SEO 
        title={t('imprint.title')}
        description="Legal information and contact details for Beyer & Jdaa IPM Consulting GbR - Impressum according to German law."
        keywords="Imprint, Impressum, Legal Information, Contact Details, German Law"
      />
      <Breadcrumbs />
      <Container>
        <BackLink to="/">
          <FaArrowLeft /> {t('commonPages.backToHome')}
        </BackLink>

        <Title>{t('imprint.title')}</Title>

        <Content>
          <div className="contact-info">
            <h2>{t('imprint.serviceProviderInfo')}</h2>
            <p>
              <strong>Beyer & Jdaa IPM Consulting GbR</strong><br />
              Bahnhofstr. 76<br />
              25715 Eddelak<br />
              Deutschland
            </p>
            <p>
              <strong>{t('imprint.representedBy')}</strong><br />
              Marco Beyer und Anass Jdaa
            </p>
            <p>
              <strong>{t('common.email')}:</strong> info@ipm-consulting.org
            </p>
          </div>

          <h2>{t('imprint.professionalInfo')}</h2>

          <h3>{t('imprint.businessDescription')}</h3>
          <p>
            {t('imprint.businessDescriptionText')}
          </p>

          <h3>{t('imprint.servicesOffered')}</h3>
          <ul>
            <li>{t('imprint.softwareDevelopment')}</li>
            <li>{t('imprint.devopsAutomation')}</li>
            <li>{t('imprint.qaTesting')}</li>
            <li>{t('imprint.itArchitecture')}</li>
          </ul>

          <h2>{t('imprint.legalInfo')}</h2>

          <div className="legal-section">
            <h3>{t('imprint.vatId')}</h3>
            <p>
              <strong>{t('imprint.vatIdText')}</strong><br />
              DE451769110
            </p>
          </div>

          <h3>{t('imprint.professionalQualifications')}</h3>
          <p>
            {t('imprint.professionalQualificationsText')}
          </p>

          <h2>{t('imprint.intellectualProperty')}</h2>

          <h3>{t('imprint.copyright')}</h3>
          <p>
            {t('imprint.copyrightText')}
          </p>

          <h3>{t('imprint.trademarks')}</h3>
          <p>
            {t('imprint.trademarksText')}
          </p>

          <h2>{t('imprint.liabilityInfo')}</h2>

          <h3>{t('imprint.contentAccuracy')}</h3>
          <p>
            {t('imprint.contentAccuracyText')}
          </p>

          <h3>{t('imprint.disclaimer')}</h3>
          <p>
            {t('imprint.disclaimerText')}
          </p>

          <h2>{t('imprint.disputeResolution')}</h2>

          <h3>{t('imprint.onlineDisputeResolution')}</h3>
          <p>
            {t('imprint.onlineDisputeResolutionText')} <a href="https://ec.europa.eu/consumers/odr/" target="_blank" rel="noopener noreferrer">https://ec.europa.eu/consumers/odr/</a>
          </p>

          <h3>{t('imprint.consumerDisputeResolution')}</h3>
          <p>
            {t('imprint.consumerDisputeResolutionText')}
          </p>

          <h2>{t('imprint.dataProtection')}</h2>

          <p>
            {t('imprint.dataProtectionText')} <Link to="/privacy-policy" style={{ color: 'var(--primary-600)', textDecoration: 'none' }}>{t('footer.privacyPolicy')}</Link>.
          </p>

          <h2>{t('imprint.governingLaw')}</h2>

          <p>
            {t('imprint.governingLawText')}
          </p>

          <h2>{t('imprint.contactInformation')}</h2>

          <div className="contact-info">
            <p><strong>{t('imprint.contact')}</strong></p>
            <p>
              <strong>Beyer & Jdaa IPM Consulting GbR</strong><br />
              Bahnhofstr. 76<br />
              25715 Eddelak<br />
              Deutschland<br />
              {t('common.email')}: info@ipm-consulting.org
            </p>
          </div>

          <p style={{ fontStyle: 'italic', color: 'var(--secondary-500)', marginTop: 'var(--space-2xl)' }}>
            <strong>{t('commonPages.lastUpdated')}:</strong> January 15, 2026
          </p>
        </Content>
      </Container>
    </>
  );
};

export default Imprint; 